import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import VehicleCard from '../VehicleCard';
import Button from '../ui/Button';
import apiService from '../../services/ApiService';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import { homeVehicleToCardProps, type HomeVehicleCard } from './homeTypes';
import HomeSection from './HomeSection';

interface BuildRailProps {
  title: string;
  description?: string;
  eyebrow?: string;
  vehicles: HomeVehicleCard[];
  viewAllHref?: string;
  viewAllLabel?: string;
  layout?: 'horizontal' | 'grid';
  max?: number;
  tone?: 'default' | 'muted' | 'emphasis';
  railId?: string;
}

export const BuildRail: React.FC<BuildRailProps> = ({
  title,
  description,
  eyebrow,
  vehicles,
  viewAllHref = '/discover',
  viewAllLabel = 'View all',
  layout = 'horizontal',
  max = 6,
  tone = 'default',
  railId,
}) => {
  const navigate = useNavigate();
  const items = vehicles.slice(0, max);
  if (items.length === 0) return null;

  const onViewAll = () => {
    void apiService.trackEvent('home_rail_view_all', {
      metadata: { rail: title, href: viewAllHref },
    });
  };

  return (
    <HomeSection
      id={railId}
      eyebrow={eyebrow}
      title={title}
      description={description}
      tone={tone}
      action={
        <Link
          to={viewAllHref}
          onClick={onViewAll}
          className="text-sm text-orange-300 hover:text-orange-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 rounded"
        >
          {viewAllLabel} →
        </Link>
      }
    >
      {layout === 'horizontal' ? (
        <div className="-mx-4 sm:mx-0">
          <ul className="flex gap-4 overflow-x-auto px-4 sm:px-0 pb-2 snap-x snap-mandatory motion-safe:scroll-smooth">
            {items.map((v) => (
              <li key={v.id} className="w-[260px] sm:w-[280px] flex-shrink-0 snap-start">
                <VehicleCard
                  vehicle={homeVehicleToCardProps(v)}
                  collectionMode
                  showOwner
                  onVehicleClick={(vehicle) => {
                    const slug = resolveVehicleNavSlug(vehicle);
                    if (!vehicle.owner_username || !slug) return;
                    const path = vehicleProfilePath(vehicle.owner_username, slug);
                    if (path) navigate(path);
                  }}
                  onUserClick={(username) => navigate(`/${username}`)}
                  discoveryBadges={v.badges as any}
                  discoveryBadgeOverflow={v.badge_overflow}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {items.map((v) => (
            <VehicleCard
              key={v.id}
              vehicle={homeVehicleToCardProps(v)}
              collectionMode
              showOwner
              onVehicleClick={(vehicle) => {
                const slug = resolveVehicleNavSlug(vehicle);
                if (!vehicle.owner_username || !slug) return;
                const path = vehicleProfilePath(vehicle.owner_username, slug);
                if (path) navigate(path);
              }}
              onUserClick={(username) => navigate(`/${username}`)}
              discoveryBadges={v.badges as any}
              discoveryBadgeOverflow={v.badge_overflow}
            />
          ))}
        </div>
      )}
      <div className="mt-6 sm:hidden">
        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={() => {
            onViewAll();
            navigate(viewAllHref);
          }}
        >
          {viewAllLabel}
        </Button>
      </div>
    </HomeSection>
  );
};

export default BuildRail;
